import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "goober";
import { mobile, mobileLarge, tablet } from "clutch/src/Style/style.mjs";

import GameTile from "@/app/AppShellGameTile.jsx";
import SelectDropdown from "@/app/AppShellSelectDropdown.jsx";
import {
  ACTIVE_GAMES,
  GAME_PREFERRED_NAME_MAP,
  GAMES_COMING_SOON,
} from "@/app/constants.mjs";
import { BodyDownloadButton } from "@/app/DownloadButtonWrapper.jsx";
import BlitzLogo from "@/inline-assets/blitz-logo.svg";
import EaLogo from "@/inline-assets/ea-logo.svg";
import EpicLogo from "@/inline-assets/epic-logo.svg";
import RiotLogo from "@/inline-assets/riot-logo.svg";
import ValveLogo from "@/inline-assets/valve-logo.svg";
import BlitzWordmark from "@/inline-assets/wordmark.svg";

const Page = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;

  margin-bottom: var(--sp-8);
  padding-inline: var(--pad-for-scrollbar-offset);

  background: radial-gradient(
      50% 50% at 100% -0%,
      #24131b 0%,
      rgba(36, 19, 27, 0) 100%
    ),
    radial-gradient(50% 50% at 0% 0%, #24131b 0%, rgba(36, 19, 27, 0) 100%);
  background-size: 100% 480px;
  background-repeat: no-repeat;

  .mobile {
    display: none;
  }

  ${mobileLarge} {
    padding-inline: 0;

    .desktop {
      display: none;
    }

    .mobile {
      display: flex;
    }
  }
`;

const PageContent = styled("div")`
  display: flex;
  padding-bottom: 0px;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-8);

  width: 100%;
  max-width: var(--sp-container);

  ${tablet} {
    max-width: 42rem;
  }

  ${mobileLarge} {
    display: flex;
    padding: 0;
    flex-direction: column;
    align-items: center;
    align-self: stretch;
    gap: 0;
  }
`;

const HeroSection = styled("div")`
  display: flex;
  padding-top: var(--sp-10);
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: var(--sp-6);
  align-self: stretch;
  .type-page-header {
    text-align: center;
  }

  ${mobileLarge} {
    display: flex;
    width: 100%;
    padding: var(--sp-8) var(--sp-4) var(--sp-5) var(--sp-4);
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: var(--sp-6);
    background: var(--shade8);
    border-bottom: solid var(--sp-px) var(--shade3-15);
  }
`;

const Logo = styled("div")`
  display: none;
  ${mobile} {
    display: flex;
    align-items: center;
    gap: var(--sp-3);
    align-self: stretch;
    .logo {
      height: var(--sp-6);
      width: var(--sp-6);
    }
    .watermark {
      height: var(--sp-3);
      width: var(--sp-16);
    }

    gap: var(--sp-2);
    justify-content: center;
  }
`;

const StatsSection = styled("div")`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  row-gap: var(--sp-6);

  ${tablet} {
    .download-blitz-button {
      width: 100%;
    }
  }

  ${mobileLarge} {
    .download-blitz-button {
      display: none;
    }
  }
`;

const StatsBlock = styled("div")`
  display: flex;
  align-items: center;
  align-self: stretch;
  gap: var(--sp-9);
  flex: 1;

  ${mobileLarge} {
    display: flex;
    padding: 0 var(--sp-6);
    justify-content: center;
    align-items: center;
    gap: var(--sp-8);
    align-self: stretch;
    .vertical-divider-2 {
      display: none;
    }
  }
`;

const StatsTextBlock = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--sp-1);
  ${mobileLarge} {
    flex: 1;
    align-items: center;
  }
`;

const StatsTextSubtitle = styled("span")`
  color: var(--shade0-50);
`;

const Line = styled("div")`
  width: var(--sp-px);
  height: 3rem;
  background: var(--shade1-15);
`;

const LogosBlock = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--sp-2);
  ${mobileLarge} {
    display: none;
  }
`;

const Logos = styled("div")`
  display: flex;
  align-items: center;
  gap: 0.875rem;
`;

const Games = styled("div")`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  ${tablet} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  ${mobileLarge} {
    grid-template-columns: repeat(auto-fill, minmax(6.75rem, 1fr));
  }
  grid-column-gap: var(--sp-2_5);
  grid-row-gap: var(--sp-2);
`;

const Filters = styled("div")`
  display: flex;
  align-items: baseline;
  gap: var(--sp-2);
  align-self: stretch;

  > span.type-large-title--bold {
    color: var(--shade0);
  }
`;

const GamesWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--sp-2);
  width: 100%;

  ${mobileLarge} {
    padding: var(--sp-3) var(--sp-4) var(--sp-4) var(--sp-4);

    div.mobile {
      display: grid;
    }
  }
`;

const OPTIONS: {
  text: Translation;
  value: string;
}[] = [
  {
    text: ["common:sort.popularity", "Sort by Popularity"],
    value: "popularity",
  },
  {
    text: ["common:sort.name", "Sort by Name"],
    value: "name",
  },
];

const sortGames = ({ games, t, sortBy }) => {
  if (sortBy === "name") {
    return games.slice().sort((a, b) => {
      return t(...GAME_PREFERRED_NAME_MAP[a]).localeCompare(
        t(...GAME_PREFERRED_NAME_MAP[b]),
      );
    });
  }
  return games;
};

export default function HomeLanding() {
  const { t } = useTranslation();

  const title = t(
    "home:title",
    "Personalized Stats and Insights Powered by Billions of Matches",
  );
  const [sortBy, setSortBy] = useState(OPTIONS[0].value);

  const sortedList = sortGames({
    games: [...ACTIVE_GAMES, ...GAMES_COMING_SOON],
    sortBy,
    t,
  });
  const activeGamesSortedList = sortGames({ games: ACTIVE_GAMES, sortBy, t });
  const comingSoonGamesSortedList = sortGames({
    games: GAMES_COMING_SOON,
    sortBy,
    t,
  });

  return (
    <Page>
      <PageContent>
        <HeroSection>
          <Logo>
            <BlitzLogo className="logo" />
            <BlitzWordmark className="wordmark" />
          </Logo>
          <h1 className="type-h4 desktop">{title}</h1>
          <h1 className="type-page-header mobile">{title}</h1>
          <StatsSection>
            <StatsBlock>
              <StatsTextBlock>
                <h6 className="type-large-title--bold">
                  {t("common:stats.players", "{{ players }}", {
                    players: "240M+",
                  })}
                </h6>
                <StatsTextSubtitle className="type-callout">
                  {t("common:search.players", "Players")}
                </StatsTextSubtitle>
              </StatsTextBlock>
              <Line />
              <StatsTextBlock>
                <h6 className="type-large-title--bold">
                  {t("common:stats.matchesAnalyzed", "{{ matches }}", {
                    matches: "2B+",
                  })}
                </h6>
                <StatsTextSubtitle className="type-callout">
                  {t("common:matchesAnalyzed", "Matches Analyzed")}
                </StatsTextSubtitle>
              </StatsTextBlock>
              <Line className="vertical-divider-2" />
              <LogosBlock>
                <Logos>
                  <RiotLogo />
                  <EpicLogo />
                  <EaLogo />
                  <ValveLogo />
                </Logos>
                <StatsTextSubtitle className="type-callout">
                  {t("welcome:header.compliant", "Fully Compliant")}
                </StatsTextSubtitle>
              </LogosBlock>
            </StatsBlock>
            <BodyDownloadButton
              isPrimary={true}
              size="large"
              textClass="type-body2-form--active"
              className="download-blitz-button body"
              target="_blank"
              uiLocation="HOME_BODY"
            />
          </StatsSection>
        </HeroSection>
        <GamesWrapper>
          <Filters>
            <span className="type-large-title--bold">
              {t("common:allGames", "All Games")}
            </span>

            <SelectDropdown
              selected={sortBy}
              options={OPTIONS}
              onChange={setSortBy}
            />
          </Filters>
          <Games className="desktop">
            {sortedList.map((gameSymbol, key) => (
              <GameTile key={key} gameSymbol={gameSymbol} />
            ))}
          </Games>
          <Games className="mobile">
            {activeGamesSortedList.map((gameSymbol, key) => (
              <GameTile key={key} gameSymbol={gameSymbol} />
            ))}
          </Games>
          <span className="mobile type-large-title--bold">
            {t("common:comingSoonNormal", "Coming Soon")}
          </span>
          <Games className="mobile">
            {comingSoonGamesSortedList.map((gameSymbol, key) => (
              <GameTile key={key} gameSymbol={gameSymbol} />
            ))}
          </Games>
        </GamesWrapper>
      </PageContent>
    </Page>
  );
}
